import { Badge } from "./ui/Badge";
import { SafeLink } from "./ui/SafeLink";
import { ExternalLinkIcon } from "./ui/icons";
import { calendar as calendarCopy, verificationStatus } from "../copy/sq";
import { formatDate } from "../lib/date";
import type { CalendarEvent } from "../types/api";

const VERIFICATION_TONE: Record<string, "good" | "warn" | "neutral"> = {
  verified: "good",
  partially_verified: "warn",
  unverified: "neutral",
};

/** verification_status is a small fixed vocabulary (translated via copy/sq.ts); category is open, uncontrolled text (see that file's header) — shown lightly formatted, never mistranslated. */
function verificationLabel(status: string): string {
  return verificationStatus[status] ?? status.replace(/_/g, " ");
}

function EventCard({ event }: { event: CalendarEvent }) {
  return (
    <div className="rounded-lg border border-ink-100 bg-white p-4">
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <Badge tone="accent">{calendarCopy.systemEvent}</Badge>
        {event.category ? <Badge tone="neutral">{event.category.replace(/_/g, " ")}</Badge> : null}
        <Badge tone={VERIFICATION_TONE[event.verification_status] ?? "neutral"}>{verificationLabel(event.verification_status)}</Badge>
      </div>
      <p className="font-serif text-sm font-semibold leading-snug text-ink-950">{event.title}</p>
      {event.description ? <p className="mt-1.5 text-sm leading-relaxed text-ink-600">{event.description}</p> : null}
      <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-400">
        {event.source_name ? <span>{event.source_name}</span> : null}
        {event.source_date ? <span>{calendarCopy.sourcedOn(formatDate(event.source_date))}</span> : null}
        {event.source_url ? (
          <SafeLink href={event.source_url} className="inline-flex items-center gap-1 font-medium text-accent-600 hover:text-accent-700">
            {calendarCopy.original} <ExternalLinkIcon width={13} height={13} />
          </SafeLink>
        ) : null}
      </div>
    </div>
  );
}

export function EventPanel({ date, events }: { date: Date | null; events: CalendarEvent[] }) {
  if (!date) {
    return <p className="px-1 text-sm text-ink-400">{calendarCopy.selectPrompt}</p>;
  }

  return (
    <div>
      <p className="mb-3 px-1 text-xs font-semibold uppercase tracking-wide text-ink-400">
        {date.toLocaleDateString("sq-AL", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
      </p>
      {events.length === 0 ? (
        <p className="px-1 text-sm text-ink-400">{calendarCopy.noEvents}</p>
      ) : (
        <div className="space-y-2.5">
          {events.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </div>
      )}
    </div>
  );
}
